import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";

const navItems = [
  { label: "Leistungen", to: "/#leistungen" },
  { label: "Über", to: "/#ueber" },
  { label: "Kontakt", to: "/#kontakt" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-md border-b border-border shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 lg:px-8">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-1"
        >
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-lg font-bold text-foreground">Computer Hellen</div>
            <div className="text-xs text-muted-foreground">IT Dienstleistungen seit 2004</div>
          </div>
          <img
            src="/assets/logo-mouse.png"
            alt="Computer Hellen Logo"
            className="h-12 w-auto -ml-4 sm:-ml-5 -mt-3"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {navItems.map((item) => (
            <a
              key={item.to}
              href={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
            >
              {item.label}
            </a>
          ))}
          <NavLink
            to="/impressum"
            data-testid="nav-impressum"
            className={({ isActive }) =>
              `text-sm font-medium transition-colors ${isActive ? "text-primary" : "text-foreground/80 hover:text-primary"}`
            }
          >
            Impressum
          </NavLink>
          <a
            href="tel:028349448700"
            data-testid="navbar-call-button"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition"
          >
            <Phone className="h-4 w-4" />
            02834 9448700
          </a>
        </nav>

        <button
          onClick={() => setOpen(!open)}
          data-testid="navbar-mobile-toggle"
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-white"
          aria-label="Menü"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-white" data-testid="navbar-mobile-menu">
          <div className="px-5 py-4 space-y-3">
            {navItems.map((item) => (
              <a
                key={item.to}
                href={item.to}
                className="block py-2 text-base font-medium text-foreground"
                data-testid={`nav-mobile-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/impressum"
              className="block py-2 text-base font-medium text-foreground"
              data-testid="nav-mobile-impressum"
            >
              Impressum
            </Link>
            <a
              href="tel:028349448700"
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground"
              data-testid="navbar-mobile-call-button"
            >
              <Phone className="h-4 w-4" />
              02834 9448700
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
